import {
  type PatientDocumentDeliveries,
  PatientDocumentDeliveriesSchema,
  type RevokePatientDocumentDeliveryResponse,
  RevokePatientDocumentDeliveryResponseSchema,
  type SendPatientDocumentRequest,
  SendPatientDocumentRequestSchema,
  type SendPatientDocumentResponse,
  SendPatientDocumentResponseSchema,
} from "@emr/contracts";
import { z } from "zod";
import { schedulingRequestError } from "../demo/schedulingErrors";

/** Staff document delivery acts on the authenticated client's site; reads are audited. */
export function createPatientDocumentDeliveriesClient(
  request: (path: string, body?: unknown, signal?: AbortSignal) => Promise<Response>,
) {
  const uuid = (id: string) => encodeURIComponent(z.string().uuid().parse(id));
  const path = (patientId: string) => `/v1/patients/${uuid(patientId)}/document-deliveries`;
  async function json<T extends z.ZodType>(
    url: string,
    schema: T,
    fallback: string,
    body?: unknown,
    signal?: AbortSignal,
  ): Promise<z.output<T>> {
    const response = await request(url, body, signal);
    if (!response.ok) throw await schedulingRequestError(response, fallback);
    return schema.parse(await response.json());
  }
  return {
    getPatientDocumentDeliveries(
      patientId: string,
      signal?: AbortSignal,
    ): Promise<PatientDocumentDeliveries> {
      return json(
        path(patientId),
        PatientDocumentDeliveriesSchema,
        "Patient document deliveries could not be loaded.",
        undefined,
        signal,
      );
    },
    sendPatientDocument(
      patientId: string,
      input: SendPatientDocumentRequest,
      signal?: AbortSignal,
    ): Promise<SendPatientDocumentResponse> {
      return json(
        path(patientId),
        SendPatientDocumentResponseSchema,
        "The document could not be sent.",
        SendPatientDocumentRequestSchema.parse(input),
        signal,
      );
    },
    revokePatientDocumentDelivery(
      patientId: string,
      deliveryId: string,
      signal?: AbortSignal,
    ): Promise<RevokePatientDocumentDeliveryResponse> {
      return json(
        `${path(patientId)}/${uuid(deliveryId)}/revoke`,
        RevokePatientDocumentDeliveryResponseSchema,
        "The document link could not be revoked.",
        {},
        signal,
      );
    },
  };
}

export type PatientDocumentDeliveriesClient = ReturnType<
  typeof createPatientDocumentDeliveriesClient
>;
export const patientDocumentDeliveriesKey = (siteId: string, patientId: string) =>
  ["patient-document-deliveries", siteId, patientId] as const;
